import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { ApiError, api } from "../lib/api.js";

interface Props {
  forced?: boolean;
}

export function ChangePasswordPage({ forced = false }: Props) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  // Se o usuário tem 2FA ativado, o backend exige `twoFactorCode` na troca de
  // senha. Consultamos o status uma vez para saber se precisamos mostrar o
  // campo. Em caso de erro (sessão expirada, etc.), silenciamos — a validação
  // final é sempre do backend.
  useEffect(() => {
    if (forced) return; // primeiro acesso: usuário ainda não pode ter 2FA
    api.twoFactor
      .status()
      .then((s) => setTwoFactorEnabled(s.enabled))
      .catch(() => setTwoFactorEnabled(false));
  }, [forced]);

  const rules = [
    { label: "mínimo 10 caracteres", ok: next.length >= 10 },
    { label: "uma letra minúscula", ok: /[a-z]/.test(next) },
    { label: "uma letra maiúscula", ok: /[A-Z]/.test(next) },
    { label: "um número", ok: /\d/.test(next) },
    { label: "diferente da atual", ok: next.length > 0 && next !== current },
    { label: "confirmação idêntica", ok: next.length > 0 && next === confirm },
  ];
  const allOk = rules.every((r) => r.ok);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!allOk) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, string> = {
        currentPassword: current,
        newPassword: next,
      };
      if (twoFactorEnabled) body.twoFactorCode = twoFactorCode;
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload: unknown = await res.json().catch(() => ({}));
        const code =
          (payload as { error?: string }).error ?? String(res.status);
        if (code === "2fa_required")
          setError("Código 2FA inválido ou ausente.");
        else if (res.status === 401) setError("Senha atual incorreta.");
        else if (code === "same_password")
          setError("Escolha uma senha diferente da atual.");
        else if (res.status === 429)
          setError("Muitas tentativas. Tente em alguns minutos.");
        else setError("Erro ao trocar senha.");
        return;
      }
      setOk(true);
      await refresh();
      setTimeout(() => nav("/app", { replace: true }), 800);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Erro de rede.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={forced ? "nx-login" : ""}>
      {!forced && (
        // Fora do fluxo forçado, "Trocar senha" ganha o mesmo cabeçalho
        // padrão das demais páginas: eyebrow situa o operador na categoria
        // Segurança, e o sub explica o que vai acontecer.
        <div className="nx-page-head">
          <div className="nx-page-head__title">
            <span className="nx-page-head__eyebrow">Segurança</span>
            <h1>Trocar senha</h1>
            <div className="nx-page-head__sub">
              Você precisa da senha atual
              {twoFactorEnabled ? " e do código 2FA" : ""}. Após a troca, sua
              sessão continua ativa.
            </div>
          </div>
        </div>
      )}
      <div className={forced ? "nx-login__card" : "nx-card"}>
        {forced && (
          <>
            <div className="nx-login__brand">
              NX<span>volt</span>
            </div>
            <div className="nx-login__parent">uma solução Noxxon Sat</div>

            <h1 className="nx-login__title">Defina sua senha</h1>
            <p
              style={{
                color: "var(--nx-mute)",
                marginTop: "var(--nx-space-2)",
              }}
            >
              Este é seu primeiro acesso, {user?.displayName}. Escolha uma nova
              senha para continuar.
            </p>
          </>
        )}

        <form onSubmit={onSubmit} noValidate>
          <label className="nx-field">
            <span>Senha atual</span>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              autoFocus
            />
          </label>
          <label className="nx-field">
            <span>Nova senha</span>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label className="nx-field">
            <span>Confirmar nova senha</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {twoFactorEnabled && (
            <label className="nx-field">
              <span>Código 2FA atual</span>
              <input
                type="text"
                inputMode="numeric"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                autoComplete="one-time-code"
                required
                className="mono"
                placeholder="000000"
                maxLength={9}
              />
            </label>
          )}

          <ul className="nx-rules" aria-live="polite">
            {rules.map((r) => (
              <li key={r.label} className={r.ok ? "nx-rules__ok" : ""}>
                {r.ok ? "✓" : "○"} {r.label}
              </li>
            ))}
          </ul>

          {error && (
            <div role="alert" className="nx-error">
              {error}
            </div>
          )}
          {ok && (
            <div className="nx-success" role="status">
              ✓ Senha trocada. Redirecionando…
            </div>
          )}

          <button
            className="nx-btn nx-btn--full"
            type="submit"
            disabled={submitting || !allOk}
          >
            {submitting ? "Salvando…" : "Trocar senha"}
          </button>
        </form>
      </div>
    </div>
  );
}
